import { Location } from '@angular/common';
import { ChangeDetectionStrategy, Component, NgZone } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { addMinutes, format } from 'date-fns';
import {
  BehaviorSubject,
  map,
  Observable,
  shareReplay,
  Subject,
  takeUntil,
} from 'rxjs';
import { defaultCancelReasons } from 'server/helpers/defaultCancelReasons';
import { Customers } from 'server/shared/collections/customers';
import { Users } from 'server/shared/collections/users';
import { ArchiveAtTypeEnum } from 'server/shared/models/archiveAtEnum';
import { CancelReason } from 'server/shared/models/cancelReason';
import { Certificate } from 'server/shared/models/certificate';
import { CertificateGroup } from 'server/shared/models/certificateGroup';
import { Customer, CustomerTypeEnum } from 'server/shared/models/customer';
import { CustomerFlagsInput } from 'server/shared/models/CustomerFlagsInput';
import { RoleEnum } from 'server/shared/models/role.enum';
import { ShiftEnum } from 'server/shared/models/shift.enum';
import { DispoUser } from 'server/shared/models/user';
import { CertificateService } from 'src/app/services/certificate.service';
import { i18nPipeTransform } from 'src/helpers/translationHelpers';
import { ToastService } from '../../services/toast/toast.service';
import { TranslationService } from '../../services/translation.service';
import { ConfirmationDialog } from '../../shared/confirmation-dialog/confirmation.component';

@Component({
  selector: 'admin-customer-flags',
  templateUrl: 'customer-flags.html',
  styleUrls: ['customer-flags.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CustomerFlagsComponent {
  private destroyed$ = new Subject<void>();
  customer$ = new BehaviorSubject<Customer | undefined>(undefined);
  user$ = new BehaviorSubject<DispoUser | undefined>(undefined);

  customerTypeEnum = CustomerTypeEnum;
  shiftEnum = ShiftEnum;

  preventAutoDirectAcceptWithinHours$ = new BehaviorSubject<{
    [key in ShiftEnum]: number;
  }>({
    [ShiftEnum.EARLY]: 0,
    [ShiftEnum.DAY]: 0,
    [ShiftEnum.LATE]: 0,
    [ShiftEnum.NIGHT]: 0,
  });

  timeTrackingDefaultPauseInHours$ = new BehaviorSubject({
    [ShiftEnum.EARLY]: 0,
    [ShiftEnum.DAY]: 0,
    [ShiftEnum.LATE]: 0,
    [ShiftEnum.NIGHT]: 0,
  });

  preventDirectAcceptWithinHoursPerShift$ = new BehaviorSubject<{
    [key in ShiftEnum]: number;
  }>({
    [ShiftEnum.EARLY]: 0,
    [ShiftEnum.DAY]: 0,
    [ShiftEnum.LATE]: 0,
    [ShiftEnum.NIGHT]: 0,
  });

  updatedFields$ = new BehaviorSubject<CustomerFlagsInput>({});

  currentUserTracker?: Tracker.Computation;

  cancelReasons$: Observable<CancelReason[]> = this.customer$.pipe(
    map((customer) => customer?.cancelReasons || defaultCancelReasons),
  );

  selectedCancelReasonIds: string[] = [];

  locale = this.translationService.getLanguage();

  customerCertificates$ = this.certificateService.readyCertificates$.pipe(
    shareReplay(1),
    takeUntil(this.destroyed$),
  );
  certificateGroups$: Observable<CertificateGroup[]> = this.customer$.pipe(
    map((customer) => customer?.certificateGroups || []),
    shareReplay(1),
    takeUntil(this.destroyed$),
  );

  enableJobRequestProcess$: Observable<boolean> = this.customer$.pipe(
    map((customer) => customer?.enableJobRequestProcess || false),
  );

  archiveAtTypeEnum = ArchiveAtTypeEnum;
  archiveOffsetDescriptionStartOfJob = '7:00';
  archiveOffsetDescriptionEndOfJob = '16:00';

  constructor(
    private ngZone: NgZone,
    private location: Location,
    private toastService: ToastService,
    private translationService: TranslationService,
    private certificateService: CertificateService,
    private dialog: MatDialog,
  ) {}

  ngOnInit(): void {
    this.currentUserTracker = Tracker.autorun(() => {
      this.ngZone.run(() => {
        const user = Users.findOne({
          _id: Meteor.userId(),
          role: RoleEnum.DISPO,
        }) as DispoUser;

        let customer = Customers.findOne({ _id: user?.customerId });

        if (
          user &&
          !user.dispoPermissions?.dispoAccess?.featurePermissions
            ?.isCustomerAdmin
        ) {
          this.historyBack();
        }
        this.customer$.next(customer);
        this.user$.next(user);
        this.setUpdatedFields();
      });
    });
  }

  setUpdatedFields() {
    const customer = this.customer$.value;
    const updatedFields: CustomerFlagsInput = {};

    if (
      customer?.autoAddTimeTrackingHours &&
      customer?.autoAddTimeTrackingHours < 1
    ) {
      customer.autoAddTimeTrackingHours = 1;
    }
    if (
      customer?.timeTrackingAutoApproveInDays &&
      customer?.timeTrackingAutoApproveInDays < 1
    ) {
      customer.timeTrackingAutoApproveInDays = 1;
    }

    updatedFields.enablePoolerTypes = customer?.enablePoolerTypes || false;
    updatedFields.enableDemandReasons = customer?.enableDemandReasons || false;
    updatedFields.prioritizeEmployeeLastName =
      customer?.prioritizeEmployeeLastName || false;
    updatedFields.transferredJobsNoLongerActionRequired =
      customer?.transferredJobsNoLongerActionRequired || false;
    updatedFields.approvedJobRequestsNoLongerActionRequired =
      customer?.approvedJobRequestsNoLongerActionRequired || false;
    updatedFields.enableJobRequestApproval =
      customer?.enableJobRequestApproval || false;
    updatedFields.enableCaseNumberInJobRequests =
      customer?.enableCaseNumberInJobRequests || false;
    updatedFields.requestersCanAccessReports =
      customer?.requestersCanAccessReports || false;
    updatedFields.requestersCanAccessJobOverviewReport =
      customer?.requestersCanAccessJobOverviewReport || false;
    updatedFields.enableAutoDirectAccept =
      customer?.enableAutoDirectAccept || false;
    this.preventAutoDirectAcceptWithinHours$.next(
      customer?.preventAutoDirectAcceptWithinHours || {
        [ShiftEnum.EARLY]: 0,
        [ShiftEnum.DAY]: 0,
        [ShiftEnum.LATE]: 0,
        [ShiftEnum.NIGHT]: 0,
      },
    );
    updatedFields.enableCopyDateOnDuplicate =
      customer?.enableCopyDateOnDuplicate || false;
    updatedFields.allowIgnoreUserPreferenceDepartmentBlacklist =
      customer?.allowIgnoreUserPreferenceDepartmentBlacklist || false;
    updatedFields.preventChangingOfCreator =
      customer?.preventChangingOfCreator || false;
    updatedFields.disableDispoFilterSave =
      customer?.disableDispoFilterSave || false;
    updatedFields.timeTrackingEnabled = customer?.timeTrackingEnabled || false;
    updatedFields.timeTrackingDescriptionRequiredOnChange =
      customer?.timeTrackingDescriptionRequiredOnChange || false;
    updatedFields.enableTimeTrackingSigninProcess =
      customer?.enableTimeTrackingSigninProcess || false;
    updatedFields.autoAddTimeTrackingHours =
      customer?.autoAddTimeTrackingHours || 0;
    updatedFields.timeTrackingAutoApproveInDays =
      customer?.timeTrackingAutoApproveInDays || 0;
    this.timeTrackingDefaultPauseInHours$.next(
      customer?.timeTrackingDefaultPauseInHours || {
        [ShiftEnum.EARLY]: 0,
        [ShiftEnum.DAY]: 0,
        [ShiftEnum.LATE]: 0,
        [ShiftEnum.NIGHT]: 0,
      },
    );
    updatedFields.disableTimetrackingForOwnPoolers =
      customer?.disableTimetrackingForOwnPoolers || false;
    updatedFields.disableTimetrackingForNewOrganisationsByDefault =
      customer?.disableTimetrackingForNewOrganisationsByDefault || false;
    updatedFields.disableApproveTimetrackingNotificationsForTransfers =
      customer?.disableApproveTimetrackingNotificationsForTransfers || false;
    updatedFields.enableRequestOverview =
      customer?.enableRequestOverview || false;
    updatedFields.enableSectionToggleInJobMatrix =
      customer?.enableSectionToggleInJobMatrix || false;
    updatedFields.jobMatrixHideCanceledJobs =
      customer?.jobMatrixHideCanceledJobs || false;
    updatedFields.jobMatrixForRequester =
      customer?.jobMatrixForRequester || false;
    updatedFields.enableMonthlyHours = customer?.enableMonthlyHours || false;
    updatedFields.enablePerigonApi = customer?.enablePerigonApi || false;
    updatedFields.perigonBearerToken = customer?.perigonBearerToken || '';
    updatedFields.autoBookInvitees = customer?.autoBookInvitees || false;
    updatedFields.autoBookSelfSignIns = customer?.autoBookSelfSignIns || false;
    updatedFields.autoBookInviteesDepartmentDefault =
      customer?.autoBookInviteesDepartmentDefault || false;
    updatedFields.autoBookSelfSignInsDepartmentDefault =
      customer?.autoBookSelfSignInsDepartmentDefault || false;
    updatedFields.jobMatrixCancelReasonExceptions =
      customer?.jobMatrixCancelReasonExceptions || [];
    this.selectedCancelReasonIds =
      customer?.jobMatrixCancelReasonExceptions?.map(
        (exception) => exception.id,
      ) || [];
    updatedFields.jobMatrixCertificatePriority =
      customer?.jobMatrixCertificatePriority?.sort(
        (a, b) => a.priority - b.priority,
      ) || [];
    let preventDirectAcceptWithinHoursPerShift = {
      [ShiftEnum.EARLY]: 0,
      [ShiftEnum.DAY]: 0,
      [ShiftEnum.LATE]: 0,
      [ShiftEnum.NIGHT]: 0,
    };
    if (customer?.preventDirectAcceptWithinSecondsPerShift) {
      preventDirectAcceptWithinHoursPerShift[ShiftEnum.EARLY] =
        customer.preventDirectAcceptWithinSecondsPerShift[ShiftEnum.EARLY] /
        3600;
      preventDirectAcceptWithinHoursPerShift[ShiftEnum.DAY] =
        customer.preventDirectAcceptWithinSecondsPerShift[ShiftEnum.DAY] / 3600;
      preventDirectAcceptWithinHoursPerShift[ShiftEnum.LATE] =
        customer.preventDirectAcceptWithinSecondsPerShift[ShiftEnum.LATE] /
        3600;
      preventDirectAcceptWithinHoursPerShift[ShiftEnum.NIGHT] =
        customer.preventDirectAcceptWithinSecondsPerShift[ShiftEnum.NIGHT] /
        3600;
    }
    this.preventDirectAcceptWithinHoursPerShift$.next(
      preventDirectAcceptWithinHoursPerShift,
    );
    updatedFields.requiredPause = customer?.requiredPause || 1;
    updatedFields.enableTranscribeJobList =
      customer?.enableTranscribeJobList || false;
    updatedFields.archiveAtType =
      customer?.archiveAtType || ArchiveAtTypeEnum.END_OF_JOB;
    updatedFields.archiveAtOffset = customer?.archiveAtOffset || 0;
    this.updatedFields$.next(updatedFields);
    this.updateArchiveAtOffset();
  }

  ngOnDestroy() {
    this.currentUserTracker?.stop();
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  cancelChanges() {
    this.setUpdatedFields();
  }

  toggleTimeTrackingEnabled(event: Event) {
    const currentFields = this.updatedFields$.value;
    const enabling = !currentFields.timeTrackingEnabled;
    event.preventDefault();

    const dialogRef = this.dialog.open(ConfirmationDialog, {
      data: {
        id: 'toggleTimeTrackingEnabled',
        title: i18nPipeTransform(
          enabling ? 'enableTimeTracking' : 'disableTimeTracking',
        ),
        text: i18nPipeTransform(
          enabling
            ? 'enableTimeTrackingDescription'
            : 'disableTimeTrackingDescription',
        ),
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.updatedFields$.next({
          ...this.updatedFields$.value,
          timeTrackingEnabled: enabling,
        });
      }
    });
  }

  updateDisableTimetrackingForNewOrganisationsByDefault(value: boolean) {
    const currentFields = this.updatedFields$.value;
    this.updatedFields$.next({
      ...currentFields,
      disableTimetrackingForNewOrganisationsByDefault: value,
    });
  }

  updateRequestersCanAccessReports(value: boolean) {
    const currentFields = this.updatedFields$.value;
    this.updatedFields$.next({
      ...currentFields,
      requestersCanAccessReports: value,
      // Automatically enable the Job Overview Report when enabling reports
      requestersCanAccessJobOverviewReport: value
        ? true
        : currentFields.requestersCanAccessJobOverviewReport,
    });
  }

  historyBack() {
    this.location.back();
  }

  saveCustomerFlags(): void {
    const customer = this.customer$.value;
    if (!customer) return;

    const dialogRef = this.dialog.open(ConfirmationDialog, {
      data: {
        id: customer._id,
        text: i18nPipeTransform('confirmSaveCustomerFlags'),
      },
    });

    dialogRef.afterClosed().subscribe((result: any) => {
      if (result) {
        const updatedFields = this.updatedFields$.value;
        if (updatedFields.enableAutoDirectAccept) {
          updatedFields.preventAutoDirectAcceptWithinHours =
            this.preventAutoDirectAcceptWithinHours$.value;
        }
        if (updatedFields.timeTrackingEnabled) {
          updatedFields.timeTrackingDefaultPauseInHours =
            this.timeTrackingDefaultPauseInHours$.value;
        }

        let preventDirectAcceptWithinSecondsPerShift = {
          [ShiftEnum.EARLY]:
            this.preventDirectAcceptWithinHoursPerShift$.value[
              ShiftEnum.EARLY
            ] * 3600,
          [ShiftEnum.DAY]:
            this.preventDirectAcceptWithinHoursPerShift$.value[ShiftEnum.DAY] *
            3600,
          [ShiftEnum.LATE]:
            this.preventDirectAcceptWithinHoursPerShift$.value[ShiftEnum.LATE] *
            3600,
          [ShiftEnum.NIGHT]:
            this.preventDirectAcceptWithinHoursPerShift$.value[
              ShiftEnum.NIGHT
            ] * 3600,
        };

        updatedFields.preventDirectAcceptWithinSecondsPerShift =
          preventDirectAcceptWithinSecondsPerShift;

        Meteor.call(
          'updateCustomerFlags',
          customer._id,
          updatedFields,
          (error: Meteor.Error) => {
            if (error) {
              this.toastService.add({
                type: 'ERROR',
                message: error.error?.toString(),
              });
            } else {
              this.toastService.add({
                type: 'SUCCESS',
                message: 'changesSaved',
              });
            }
          },
        );
      }
    });
  }

  selectCancelReason(reasonIds: any[], cancelReasons: CancelReason[]) {
    this.selectedCancelReasonIds = reasonIds;

    const currentFields = this.updatedFields$.value;
    currentFields.jobMatrixCancelReasonExceptions = cancelReasons?.filter(
      (reason) => this.selectedCancelReasonIds.includes(reason.id),
    );
    this.updatedFields$.next(currentFields);
  }

  increaseCertificatePriority(index: number) {
    const currentFields = this.updatedFields$.value;
    let currentPriority = currentFields.jobMatrixCertificatePriority?.[index];
    let previousPriority =
      currentFields.jobMatrixCertificatePriority?.[index - 1];

    currentFields.jobMatrixCertificatePriority?.splice(index - 1, 1, {
      priority: index - 1,
      certificates: currentPriority?.certificates || [],
    });
    currentFields.jobMatrixCertificatePriority?.splice(index, 1, {
      priority: index,
      certificates: previousPriority?.certificates || [],
    });
    this.updatedFields$.next(currentFields);
  }

  decreaseCertificatePriority(index: number) {
    const currentFields = this.updatedFields$.value;

    let currentPriority = currentFields.jobMatrixCertificatePriority?.[index];
    let nextPriority = currentFields.jobMatrixCertificatePriority?.[index + 1];

    currentFields.jobMatrixCertificatePriority?.splice(index + 1, 1, {
      priority: index + 1,
      certificates: currentPriority?.certificates || [],
    });
    currentFields.jobMatrixCertificatePriority?.splice(index, 1, {
      priority: index,
      certificates: nextPriority?.certificates || [],
    });

    this.updatedFields$.next(currentFields);
  }

  updateCertificatePriority(index: number, certificates: Certificate[]) {
    console.log(certificates);

    const currentFields = this.updatedFields$.value;
    const priority = currentFields.jobMatrixCertificatePriority?.[index];
    if (priority) {
      priority.certificates = certificates.map((certificate) => ({
        id: certificate._id,
        displayName: certificate.name,
      }));
    }
    console.log(priority?.certificates);

    this.updatedFields$.next(currentFields);
  }

  addNewCertificatePriority() {
    const currentFields = this.updatedFields$.value;
    currentFields.jobMatrixCertificatePriority?.push({
      priority: currentFields.jobMatrixCertificatePriority?.length || 0,
      certificates: [],
    });
    this.updatedFields$.next(currentFields);
  }

  updateArchiveAtOffset() {
    let baseStartTime = new Date();
    baseStartTime.setHours(7, 0, 0, 0);
    this.archiveOffsetDescriptionStartOfJob = format(
      addMinutes(baseStartTime, this.updatedFields$.value.archiveAtOffset || 0),
      'HH:mm',
    );

    let baseEndTime = new Date();
    baseEndTime.setHours(16, 0, 0, 0);
    this.archiveOffsetDescriptionEndOfJob = format(
      addMinutes(baseEndTime, this.updatedFields$.value.archiveAtOffset || 0),
      'HH:mm',
    );
  }
}
